import { ReactNode, useCallback, useMemo, useState } from 'react'

import { Group, LocalPolice, Person, RemoveRedEye, SaveAs } from '@mui/icons-material'
import { Avatar, Typography } from '@mui/material'
import { OrgEnvironmentRoleRamMember, OrgEnvironmentRoleRamMemberRolesEnum } from '@nativewaves/nw-cloud-sdk/org'
import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { CopyId } from '@shared/components/Table/Fields'
import { RoleSelector } from 'components/RoleSelector'
import { resolveDisplayName } from 'utils/userInfo'

const iconVariantMap = {
  Admin: <LocalPolice fontSize="inherit" color="inherit" />,
  Write: <SaveAs fontSize="inherit" color="inherit" />,
  Read: <RemoveRedEye fontSize="inherit" color="inherit" />,
} satisfies Record<
  (typeof OrgEnvironmentRoleRamMemberRolesEnum)[keyof typeof OrgEnvironmentRoleRamMemberRolesEnum],
  ReactNode
>

export const useColumns = ({
  onUpdate,
}: {
  onUpdate: (props: { memberId: string; roles: OrgEnvironmentRoleRamMemberRolesEnum[] }) => void
}) => {
  const { t } = useTranslation(['common', 'domain'])

  const identityUserId: string | undefined = undefined // TODO: get from /userinfo

  const [ownUserUnlocked, setOwnUserUnlocked] = useState(false)

  const handleUnlockOwnUser = useCallback(() => {
    setOwnUserUnlocked(true)
  }, [])

  return useMemo<ColumnDef<OrgEnvironmentRoleRamMember>[]>(
    () => [
      {
        accessorKey: 'memberType',
        header: () => null,
        meta: {
          sizing: {
            width: 64,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => (
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'background.levels.900' }}>
            {getValue<string>() === 'User' && <Person fontSize="inherit" color="info" />}
            {getValue<string>() === 'Team' && <Group fontSize="inherit" color="info" />}
          </Avatar>
        ),
      },
      {
        accessorFn: (row) => (row.memberType === 'User' ? resolveDisplayName(row.userInfo) : row.teamInfo?.name),
        id: '_name',
        header: t('name'),
        meta: {
          sizing: {
            minWidth: 300,
          },
        },
        cell: ({ getValue, row }) => (
          <Typography
            sx={{ color: (theme) => (!getValue() ? theme.palette.action.disabled : 'inherit') }}
            variant="inherit"
            noWrap
          >
            {getValue<string | undefined>() ? (
              <>{getValue<string>()}</>
            ) : (
              ((row.original.memberType === 'User' && t('domain:EnvironmentSetting.userWithoutName')) ??
              (row.original.memberType === 'Team' && t('domain:EnvironmentSetting.teamWithoutName')))
            )}
          </Typography>
        ),
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
      {
        accessorFn: (row) => row.userInfo?.belongsToNativeWaves,
        id: '_nw_user',
        header: () => null,
        meta: {
          align: 'end',
          sizing: {
            width: 100,
            px: 2,
          },
        },
        cell: ({ getValue }) =>
          !!getValue<boolean | undefined>() && (
            <CodeInline sx={{ py: 0, my: 0, color: 'primary.main', ml: 1 }}>
              {t('domain:EnvironmentSetting.nWUser')}
            </CodeInline>
          ),
      },
      {
        accessorKey: 'roles',
        header: () => null,
        meta: {
          align: 'end',
          sizing: {
            minWidth: 280,
            maxWidth: 320,
            px: 6,
          },
        },
        cell: ({ getValue, row }) => (
          <RoleSelector
            baseRoles={[
              OrgEnvironmentRoleRamMemberRolesEnum.Admin,
              OrgEnvironmentRoleRamMemberRolesEnum.Write,
              OrgEnvironmentRoleRamMemberRolesEnum.Read,
            ]}
            roles={getValue<OrgEnvironmentRoleRamMemberRolesEnum[]>()}
            onUpdate={({ roles }) => onUpdate({ roles, memberId: row.original.id })}
            iconVariantMap={iconVariantMap}
            isOwnUser={row.original.id.replace('user-', '') === identityUserId}
            isOwnUserUnlocked={ownUserUnlocked}
            unlockOwnUser={handleUnlockOwnUser}
          />
        ),
      },
    ],
    [t, identityUserId, ownUserUnlocked, handleUnlockOwnUser, onUpdate],
  )
}
